/**
 * RBAC Context — Phase-2.
 *
 * Hydrates the backend `/workflow/rbac/manifest` on login and exposes it
 * to the rest of the app via `useRbac()`. Components should use this
 * instead of inline role checks so Phase-3 page rollouts stay consistent
 * when new roles or pages are added.
 *
 * Usage:
 *   const { role, canWrite, visiblePages, loading } = useRbac()
 *   if (canWrite('playground-oracle')) { ... }
 *
 * Purely additive — existing AuthContext is untouched.
 */
import {
  createContext, useContext, useEffect, useState, useCallback, useMemo,
  ReactNode,
} from 'react'
import { useAuth } from './AuthContext'
import {
  getRbacManifest, PageManifest, RbacManifest, RoleName,
} from '@/services/rbacApi'

interface RbacContextShape {
  role: RoleName | null
  spoke: boolean
  pages: PageManifest[]
  categories: string[]
  loading: boolean
  error: string | null
  /** True when the current user can take WRITE actions on the page. */
  canWrite: (pageId: string) => boolean
  /** True when the current user can VIEW the page. */
  canView: (pageId: string) => boolean
  /** Subset of pages in a given category (e.g. 'playground'). */
  pagesInCategory: (category: string) => PageManifest[]
  /** Re-fetch the manifest (e.g. after role change in Settings). */
  refresh: () => Promise<void>
}

const RbacContext = createContext<RbacContextShape | undefined>(undefined)

const EMPTY_MANIFEST: RbacManifest = {
  role: null, spoke: false, pages: [], total_pages: 0, categories: [],
}

export function RbacProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated } = useAuth()
  const [manifest, setManifest] = useState<RbacManifest>(EMPTY_MANIFEST)
  const [loading, setLoading] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)

  const refresh = useCallback(async () => {
    if (!isAuthenticated) {
      setManifest(EMPTY_MANIFEST)
      return
    }
    setLoading(true)
    setError(null)
    try {
      const m = await getRbacManifest()
      setManifest(m)
    } catch (err: any) {
      // RBAC failures must not brick the app — degrade to "viewer-ish"
      // defaults so the user still sees *something*. Log and move on.
      console.warn('[Rbac] manifest fetch failed:', err?.message || err)
      setError(err?.message || 'Failed to load RBAC manifest')
      setManifest(EMPTY_MANIFEST)
    } finally {
      setLoading(false)
    }
  }, [isAuthenticated])

  useEffect(() => { void refresh() }, [refresh])

  const byId = useMemo(() => {
    const m = new Map<string, PageManifest>()
    manifest.pages.forEach(p => m.set(p.page_id, p))
    return m
  }, [manifest.pages])

  const canWrite = useCallback((pageId: string): boolean => {
    const p = byId.get(pageId)
    if (!p) return false
    if (typeof p.can_write_for_current_user === 'boolean') {
      return p.can_write_for_current_user
    }
    if (!manifest.role) return false
    return p.write_roles.includes(manifest.role)
  }, [byId, manifest.role])

  const canView = useCallback((pageId: string): boolean => {
    return byId.has(pageId)  // manifest already filtered by role
  }, [byId])

  const pagesInCategory = useCallback((category: string): PageManifest[] => {
    return manifest.pages.filter(p => p.category === category)
  }, [manifest.pages])

  const value: RbacContextShape = {
    role: manifest.role,
    spoke: manifest.spoke,
    pages: manifest.pages,
    categories: manifest.categories,
    loading,
    error,
    canWrite,
    canView,
    pagesInCategory,
    refresh,
  }

  return <RbacContext.Provider value={value}>{children}</RbacContext.Provider>
}

export function useRbac(): RbacContextShape {
  const ctx = useContext(RbacContext)
  if (!ctx) {
    // Dev-time guard. Consumers should always be inside <RbacProvider>.
    throw new Error('useRbac must be used inside <RbacProvider>')
  }
  return ctx
}
